/**
 * Import into a track list: what a pasted block or a file says, and what of it
 * may go in. `docs/features/tracklists.md`, *Import, paste, export*.
 *
 * Two steps, both pure, so the dialog only draws:
 *
 * - **`readImport`** turns text into entries. Spaces, commas, semicolons and
 *   line breaks separate. A table — a `.csv`/`.tsv` file, or a paste with tabs
 *   in it, which is what a copied spreadsheet range is — gives **its first
 *   column only**, and that cell may itself hold several symbols. A pasted line
 *   of commas is a list of symbols, not a table: `AAPL, MSFT` is two.
 * - **`planImport`** sorts them into *will be added*, *already in* and
 *   *refused*. Resolution is `planAdd`'s, called `exact` — the Add row's paste
 *   rule, so `TSL` is refused rather than filed as TSLA. Access needs no rule
 *   here: the symbol index is narrowed to the caller's plan on the server, so
 *   anything outside it is refused with the same sentence the Add row gives.
 *
 * A table's first row that resolves to nothing is its header (`Symbol`,
 * `Ticker`) and is dropped without a word; refusing it would put a line in the
 * refused pile for every file anybody exports from a spreadsheet.
 */

import { planAdd, splitEntries } from "./track-list-add";
import type { SymbolRow } from "./symbol-index";
import type { TrackList } from "./track-lists";

/** `track_list` holds at most this many — `MAX_MEMBERS` in `app/lists/store.py`. */
export const MAX_LIST_MEMBERS = 2000;

/** The server takes at most this many per call — `MAX_PER_CALL` there. */
export const IMPORT_CHUNK = 500;

export interface ImportSource {
  entries: string[];
  /** How many of `entries` came from a table's first row — a header, maybe. */
  headerCount: number;
  /** Read as a table (first column only), for the dialog to say so. */
  table: boolean;
}

/** A file read as a table by its name; pasted text, by holding a tab. */
function isTable(text: string, fileName?: string): boolean {
  if (fileName && /\.(csv|tsv)$/i.test(fileName)) return true;
  return text.includes("\t");
}

/**
 * The first field of one CSV/TSV line, unquoted.
 *
 * Only the first is wanted, so this reads up to the first separator outside
 * quotes rather than parsing the whole line — `"AAPL, MSFT",Apple` is
 * `AAPL, MSFT`, and a doubled quote inside is one quote.
 */
export function firstField(line: string): string {
  const tab = line.includes("\t");
  const sep = tab ? "\t" : ",";
  if (!line.startsWith('"')) {
    const at = line.indexOf(sep);
    return at < 0 ? line : line.slice(0, at);
  }
  let out = "";
  for (let i = 1; i < line.length; i += 1) {
    const ch = line[i]!;
    if (ch === '"') {
      if (line[i + 1] === '"') {
        out += '"';
        i += 1;
        continue;
      }
      return out;
    }
    out += ch;
  }
  return out;
}

export function readImport(text: string, fileName?: string): ImportSource {
  // A byte-order mark from a spreadsheet's CSV would otherwise glue itself to
  // the first symbol.
  const body = text.replace(/^﻿/, "");
  if (!isTable(body, fileName)) {
    return { entries: splitEntries(body), headerCount: 0, table: false };
  }
  const lines = body.split(/\r?\n/).filter((line) => line.trim() !== "");
  const entries: string[] = [];
  let headerCount = 0;
  lines.forEach((line, index) => {
    const cell = splitEntries(firstField(line));
    if (index === 0) headerCount = cell.length;
    entries.push(...cell);
  });
  return { entries, headerCount, table: true };
}

export interface ImportRefusal {
  text: string;
  reason: string;
  /** For `refusalFor`: the value and the leg the index does not hold. */
  outside?: { value: string; leg: string };
}

export interface ImportPlan {
  /** Stored spelling, first appearance kept. */
  add: string[];
  closes: Record<string, number>;
  /** Already in the list, in stored spelling. */
  already: string[];
  refused: ImportRefusal[];
  /** Every entry that was read, header excluded. */
  found: number;
}

export function planImport(
  source: ImportSource,
  { rows, list }: { rows: readonly SymbolRow[] | undefined; list: TrackList },
): ImportPlan {
  let entries = source.entries;
  if (source.headerCount > 0 && rows) {
    const header = entries.slice(0, source.headerCount);
    const resolved = planAdd(header.join(" "), { rows, list, exact: true });
    const nothing = resolved.symbols.length === 0 && resolved.rejected.every((r) => !r.already);
    if (nothing) entries = entries.slice(source.headerCount);
  }

  const plan = planAdd(entries.join(" "), { rows, list, exact: true });
  const out: ImportPlan = { add: [], closes: {}, already: [], refused: [], found: entries.length };
  for (const rejection of plan.rejected) {
    if (rejection.already) {
      out.already.push(rejection.text.toUpperCase());
    } else {
      out.refused.push({ text: rejection.text, reason: rejection.reason, outside: rejection.outside });
    }
  }

  const room = Math.max(0, MAX_LIST_MEMBERS - list.members.length);
  out.add = plan.symbols.slice(0, room);
  for (const symbol of plan.symbols.slice(room)) {
    out.refused.push({
      text: symbol,
      reason: `${list.name} is full — a list holds at most ${MAX_LIST_MEMBERS} symbols`,
    });
  }
  for (const symbol of out.add) {
    const close = plan.closes[symbol];
    if (close !== undefined) out.closes[symbol] = close;
  }
  return out;
}

/**
 * File the plan, `IMPORT_CHUNK` at a time, in order.
 *
 * Sequential so a refusal stops the rest rather than racing it, and so the
 * count that went in is exact. Resolves to how many were added.
 */
export async function fileImport(
  plan: ImportPlan,
  listId: string,
  add: (listId: string, symbols: readonly string[], closes: Record<string, number>) => Promise<void>,
): Promise<number> {
  let done = 0;
  for (let at = 0; at < plan.add.length; at += IMPORT_CHUNK) {
    const chunk = plan.add.slice(at, at + IMPORT_CHUNK);
    const closes: Record<string, number> = {};
    for (const symbol of chunk) {
      const close = plan.closes[symbol];
      if (close !== undefined) closes[symbol] = close;
    }
    await add(listId, chunk, closes);
    done += chunk.length;
  }
  return done;
}

/**
 * What a new list is born with when it starts from a set of rows — a whole
 * screen (⋯ › New list from screen…) or a Ctrl+A selection through the star's
 * New list…. Blanks and repeats out, order kept, cut at `MAX_LIST_MEMBERS`;
 * `over` is how many the cut left out, which the dialog says before Create.
 */
export function seedNewList(symbols: readonly string[]): { symbols: string[]; over: number } {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of symbols) {
    const symbol = raw.trim().toUpperCase();
    if (!symbol || seen.has(symbol)) continue;
    seen.add(symbol);
    out.push(symbol);
  }
  return { symbols: out.slice(0, MAX_LIST_MEMBERS), over: Math.max(0, out.length - MAX_LIST_MEMBERS) };
}

/**
 * Make a list holding `symbols`, `IMPORT_CHUNK` at a time.
 *
 * The create carries the first chunk and the rest are added after it, in order:
 * the server takes at most `MAX_PER_CALL` (500) symbols per call, so one create
 * holding a whole screen was refused outright — "too many symbols". The create
 * is the call that can fail for a rule (a name taken), so it goes first and
 * nothing else is sent if it does.
 */
export async function createListInChunks<T extends { id: string }>(
  symbols: readonly string[],
  closes: Record<string, number>,
  create: (symbols: string[], closes: Record<string, number>) => Promise<T>,
  add: (listId: string, symbols: readonly string[], closes: Record<string, number>) => Promise<void>,
): Promise<T> {
  const pick = (chunk: readonly string[]) => {
    const out: Record<string, number> = {};
    for (const symbol of chunk) if (closes[symbol] !== undefined) out[symbol] = closes[symbol]!;
    return out;
  };
  const first = symbols.slice(0, IMPORT_CHUNK);
  const created = await create(first, pick(first));
  for (let at = IMPORT_CHUNK; at < symbols.length; at += IMPORT_CHUNK) {
    const chunk = symbols.slice(at, at + IMPORT_CHUNK);
    await add(created.id, chunk, pick(chunk));
  }
  return created;
}
